import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
    Box,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    InputAdornment,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {useNavigate} from 'react-router';
import {addIcrAssociation, loadAnalyticReportIds, removeIcrAssociation, searchAssociationCandidates} from '../../../firebase/firestore/icr';

const AUTHORIZED_ROLES = ['admin', 'collection_manager', 'collector'];

const ITEM_TYPE_LABELS = {
    pitch:      'Pitch',
    sitrep:     'SITREP',
    whiteboard: 'Chart',
};

const ITEM_TYPE_TOOLTIPS = {
    sitrep: 'Situation Report',
};

const ITEM_TYPE_COLORS = {
    pitch:      {bg: '#fff3e0', color: '#e65100', border: '#ffcc80'},
    sitrep:     {bg: '#e3f2fd', color: '#1565c0', border: '#90caf9'},
    whiteboard: {bg: '#f3e5f5', color: '#6a1b9a', border: '#ce93d8'},
};

function itemHref(itemType, itemId) {
    if (itemType === 'whiteboard') return `/charts/${itemId}`;
    return `/products/${itemId}`;
}

function isFintelSourceProductType(sourceProductType) {
    const lower = (sourceProductType || '').toLowerCase();
    return lower === 'analytic' || lower === 'fintel' || lower.includes('analytic');
}

/** Show sourceReportId as a pitch link when no pitch association exists yet (legacy ICRs). */
function mergeSourceReportAssociation(icr, associations) {
    const base = Array.isArray(associations) ? associations : [];
    const sourceReportId = icr?.sourceReportId;
    if (!sourceReportId) return base;

    if (isFintelSourceProductType(icr?.sourceProductType)) return base;

    const hasPitchLink = base.some(a => a.itemType === 'pitch' && a.itemId === sourceReportId);
    if (hasPitchLink) return base;

    const hasAnyPitch = base.some(a => a.itemType === 'pitch');
    if (hasAnyPitch) return base;

    const subjectMatch = (icr?.subject || '').match(/^ICR Draft:\s*(.+?)\s*\(/i);
    const itemTitle = subjectMatch?.[1]?.trim() || sourceReportId;

    return [{
        id: `source-report-${sourceReportId}`,
        itemType: 'pitch',
        itemId: sourceReportId,
        itemTitle,
        isSourceReport: true,
    }, ...base];
}

// ── Section header ────────────────────────────────────────────────────────────

const SectionHeader = ({label, count, onAdd, canEdit, tooltip}) => (
    <Stack direction="row" alignItems="center" justifyContent="space-between" px={2} pt={1.5} pb={0.5}>
        <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="caption" fontWeight={700} letterSpacing={1} textTransform="uppercase"
                        color="text.secondary" fontSize="0.65rem">
                {tooltip ? (
                    <Tooltip title={tooltip} arrow placement="top">
                        <span>{label}</span>
                    </Tooltip>
                ) : label}
            </Typography>
            {count > 0 && (
                <Typography variant="caption" color="text.disabled" fontSize="0.65rem">{count}</Typography>
            )}
        </Stack>
        {canEdit && (
            <Tooltip title={`Link ${label}`}>
                <IconButton size="small" onClick={onAdd} sx={{p: 0.4}}>
                    <AddIcon fontSize="small"/>
                </IconButton>
            </Tooltip>
        )}
    </Stack>
);

// ── Association row ───────────────────────────────────────────────────────────

const AssocRow = ({assoc, canEdit, onRemove, navigate}) => {
    const href = itemHref(assoc.itemType, assoc.itemId);
    const readOnly = assoc.isSourceReport === true;

    return (
        <ListItem
            disablePadding
            secondaryAction={
                <Stack direction="row" spacing={0.5} alignItems="center">
                    <Tooltip title="Open">
                        <IconButton size="small" sx={{p: 0.4}} onClick={() => navigate(href)}>
                            <OpenInNewIcon sx={{fontSize: 14}}/>
                        </IconButton>
                    </Tooltip>
                    {canEdit && !readOnly && (
                        <Tooltip title="Remove">
                            <IconButton size="small" sx={{p: 0.4, color: 'text.disabled'}}
                                        onClick={() => onRemove(assoc.id)}>
                                <CloseIcon sx={{fontSize: 14}}/>
                            </IconButton>
                        </Tooltip>
                    )}
                </Stack>
            }
        >
            <ListItemButton
                dense
                onClick={() => navigate(href)}
                sx={{pr: 7}}
            >
                <ListItemText
                    primary={
                        <Typography variant="body2" noWrap
                                    sx={{maxWidth: 180, fontWeight: 500, fontSize: '0.82rem'}}>
                            {assoc.itemTitle || assoc.itemId}
                        </Typography>
                    }
                />
            </ListItemButton>
        </ListItem>
    );
};

// ── Picker dialog ─────────────────────────────────────────────────────────────

const PickerDialog = ({open, onClose, defaultType, onAdd}) => {
    const [itemType, setItemType] = useState(defaultType || 'pitch');
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [adding, setAdding] = useState(null);
    const debounceRef = useRef(null);

    useEffect(() => {
        if (!open) { setQuery(''); setResults([]); return; }
        setItemType(defaultType || 'pitch');
    }, [open, defaultType]);

    const doSearch = useCallback(async (type, q) => {
        setSearching(true);
        try {
            const res = await searchAssociationCandidates(type, q);
            setResults(res);
        } catch (_) {
            setResults([]);
        } finally {
            setSearching(false);
        }
    }, []);

    useEffect(() => {
        if (!open) return;
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => doSearch(itemType, query), 300);
        return () => clearTimeout(debounceRef.current);
    }, [open, itemType, query, doSearch]);

    const handleSelect = async (candidate) => {
        if (adding) return;
        setAdding(candidate.id);
        try {
            await onAdd({itemType: candidate.itemType, itemId: candidate.id, itemTitle: candidate.title});
            onClose();
        } catch (_) {
            // error handled by parent
        } finally {
            setAdding(null);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{pb: 1}}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography fontWeight={600} fontSize="1rem">Link item to ICR</Typography>
                    <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small"/></IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent sx={{pt: '8px !important'}}>
                <Stack spacing={1.5}>
                    <Select
                        size="small"
                        value={itemType}
                        onChange={e => { setItemType(e.target.value); setQuery(''); setResults([]); }}
                        fullWidth
                    >
                        {Object.entries(ITEM_TYPE_LABELS).map(([v, l]) => (
                            <MenuItem key={v} value={v}>{l}</MenuItem>
                        ))}
                    </Select>
                    <TextField
                        size="small"
                        placeholder={`Search ${ITEM_TYPE_LABELS[itemType]}…`}
                        value={query}
                        onChange={e => setQuery(e.target.value)}
                        fullWidth
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    {searching
                                        ? <CircularProgress size={14}/>
                                        : <SearchIcon fontSize="small" sx={{color: 'text.disabled'}}/>
                                    }
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Paper variant="outlined" sx={{maxHeight: 240, overflow: 'auto'}}>
                        {results.length === 0 && !searching ? (
                            <Typography px={2} py={1.5} variant="body2" color="text.secondary">
                                {query.length > 0 ? 'No results' : 'Type to search…'}
                            </Typography>
                        ) : (
                            <List disablePadding dense>
                                {results.map(r => (
                                    <ListItem key={r.id} disablePadding>
                                        <ListItemButton
                                            onClick={() => handleSelect(r)}
                                            disabled={adding === r.id}
                                        >
                                            <ListItemText
                                                primary={r.title || r.id}
                                            />
                                            {adding === r.id && <CircularProgress size={14}/>}
                                        </ListItemButton>
                                    </ListItem>
                                ))}
                            </List>
                        )}
                    </Paper>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Typography variant="caption" color="text.disabled" sx={{flex: 1, pl: 1}}>
                    Click an item to link it
                </Typography>
            </DialogActions>
        </Dialog>
    );
};

// ── Main panel ────────────────────────────────────────────────────────────────

/**
 * IcrAssociationsPanel — shows pitches, SITREPs, and whiteboards linked to an ICR.
 * For admin / collection_manager / collector roles: add and remove controls appear.
 *
 * Props:
 *   icr      — ICR document (must have .id and .associations[])
 *   role     — current user role string
 *   onUpdate — called with the updated associations array after a change
 */
const IcrAssociationsPanel = ({icr, role, onUpdate}) => {
    const navigate = useNavigate();
    const canEdit = AUTHORIZED_ROLES.includes(role);

    const [associations, setAssociations] = useState(Array.isArray(icr?.associations) ? icr.associations : []);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [pickerType, setPickerType] = useState('pitch');
    const [removing, setRemoving] = useState(null);
    const [fintelIds, setFintelIds] = useState(() => new Set());

    useEffect(() => {
        setAssociations(Array.isArray(icr?.associations) ? icr.associations : []);
    }, [icr?.associations]);

    useEffect(() => {
        let cancelled = false;
        loadAnalyticReportIds()
            .then(ids => { if (!cancelled) setFintelIds(ids); })
            .catch(() => { if (!cancelled) setFintelIds(new Set()); });
        return () => { cancelled = true; };
    }, []);

    const displayAssociations = useMemo(() => {
        const merged = mergeSourceReportAssociation(icr, associations);
        return merged.filter(a => a.itemType !== 'pitch' || !fintelIds.has(a.itemId));
    }, [icr, associations, fintelIds]);

    const byType = (type) => displayAssociations.filter(a => a.itemType === type);

    const openPicker = (type) => { setPickerType(type); setPickerOpen(true); };

    const handleAdd = async ({itemType, itemId, itemTitle}) => {
        const assoc = await addIcrAssociation(icr.id, {itemType, itemId, itemTitle});
        if (assoc && assoc.id) {
            const next = [...associations.filter(a => !(a.itemType === itemType && a.itemId === itemId)), assoc];
            setAssociations(next);
            onUpdate?.(next);
        }
    };

    const handleRemove = async (assocId) => {
        if (removing) return;
        setRemoving(assocId);
        try {
            await removeIcrAssociation(icr.id, assocId);
            const next = associations.filter(a => a.id !== assocId);
            setAssociations(next);
            onUpdate?.(next);
        } finally {
            setRemoving(null);
        }
    };

    const renderSection = (type) => {
        const items = byType(type);
        const {bg, color, border} = ITEM_TYPE_COLORS[type];
        return (
            <Box key={type}>
                <SectionHeader
                    label={ITEM_TYPE_LABELS[type]}
                    count={items.length}
                    canEdit={canEdit}
                    onAdd={() => openPicker(type)}
                    tooltip={ITEM_TYPE_TOOLTIPS[type]}
                />
                {items.length === 0 ? (
                    <Typography px={2} pb={1} variant="body2" color="text.disabled" fontSize="0.8rem">
                        No {ITEM_TYPE_LABELS[type].toLowerCase()} linked
                    </Typography>
                ) : (
                    <List disablePadding dense>
                        {items.map(a => (
                            <AssocRow
                                key={a.id}
                                assoc={a}
                                canEdit={canEdit}
                                onRemove={handleRemove}
                                navigate={navigate}
                            />
                        ))}
                    </List>
                )}
            </Box>
        );
    };

    return (
        <>
            <Paper
                elevation={0}
                sx={{width: '100%', borderRadius: 2, border: '1px solid', borderColor: 'divider', overflow: 'hidden'}}
            >
                <Typography px={2} pt={2} pb={0.5} fontWeight={600}>Linked Items</Typography>
                <Divider sx={{mx: 0}}/>
                {['pitch', 'sitrep', 'whiteboard'].map((type, idx, arr) => (
                    <React.Fragment key={type}>
                        {renderSection(type)}
                        {idx < arr.length - 1 && <Divider sx={{mx: 2}}/>}
                    </React.Fragment>
                ))}
                <Box pb={1}/>
            </Paper>

            <PickerDialog
                open={pickerOpen}
                onClose={() => setPickerOpen(false)}
                defaultType={pickerType}
                onAdd={handleAdd}
            />
        </>
    );
};

export default IcrAssociationsPanel;
